import {Tr, TrSpace} from "@/i18n/i18n";
import {injectable} from 'inversify';
import MiniCreaturesPanel from "@/scenes.ui/mini-creatures-panel";
import Scene = Phaser.Scene;
import {Listener} from "@/store/listener";
import {Creature, Force, WarriorClass} from "@/store/level/types";
import {ArtifactSlot, UnlockState} from "@/scenes.ui/artifact-slot";
import {
    Artifact,
    ArtifactInfo,
    SpeedupInfo,
    UnlockInfo,
    UpdateInfo
} from "@/store/artifact/types";
import {UpdateArtifactPanel} from "@/scenes.ui/update-artifact-panel";
import ListenerManager from "@/managers/listener-manager";
import {OverchargePanel} from "@/scenes.ui/overcharge-panel";
import {BasePanel} from "@/scenes.ui/base-panel";
import TimeUtils from "@/utils/time-utils";
import {ArtifactSpeedupPanel} from "@/scenes.ui/artifact-speedup-panel";
import {AlertDialog} from "@/scenes.ui/alert-dialog";
import {ArtifactMaxLevelPanel} from "@/scenes.ui/artifact-max-level-panel";
import {SlotInfoPanel} from "@/scenes.ui/slot-info-panel";
import {SlotUnlockPanel} from "@/scenes.ui/slot-unlock-panel";
import {HealthTimerPanel} from "@/scenes.ui/health-timer-panel";
import {HealthRestorePanel} from "@/scenes.ui/health-restore-panel";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import {ArtifactPickPanel, Mode} from "@/scenes.ui/artifact-pick-panel";
import {ArtifactLessonService} from "@/services/artifact-lesson-service";
import {CreatureUtils} from "@/utils/creature-utils";
import {Sizes} from "@/constants/size";
import {CreatureNews, ForceDescription, PayType, Player, RestoreOptions} from "@/store/player/types";
import {AbstractCreatureService} from "@/services/abstract-creature-service";
import UnlimitedCreaturesPanel from "@/scenes.ui/unlimited-creatures-panel";
import {AddToSquadButton} from "@/scenes.ui/add-to-squad-button";
import {CreaturePickPanel} from "@/scenes.ui/creature-pick-panel";
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import OnceCaller from "@/utils/once-caller";
import {ListTextPanel} from "@/scenes.ui/list-text-panel";
import {ListService} from "@/services/list-service";
import {RewardedAdvType, ShopForce} from "@/store/shop/types";
import {ShopForcePanel} from "@/scenes.ui/shop-force-panel";
import {BuyConfirmationPanel} from "@/scenes.ui/buy-confirmation-panel";
import {LevelsInfo} from "@/store/creature/types";
import {ClassLevelPanel} from "@/scenes.ui/class-level-panel";
import {SoloIntroPanel} from "@/scenes.ui/solo-intro-panel";
import ArtifactStore from "@/store/artifact";
import CreatureStore from "@/store/creature";
import PlayerStore from "@/store/player";
import ShopStore from "@/store/shop";
import {ListBlackouter} from "@/utils/list-blackouter";
import SoundUtils from "@/utils/sound-utils";
import {InfoPanel} from "@/scenes.ui/info-panel";
import RewardedUtils from "@/utils/rewarded-utils";
import Sprite = Phaser.GameObjects.Sprite;
import Container = Phaser.GameObjects.Container;
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {BottomForcesPanel} from "@/scenes.ui/bottom-forces-panel";
import {CreaturesGridView} from "@/scenes.ui/creatures-grid-view";
import {ForceSlot} from "@/scenes.ui/force-slot";
import {UpdateForcePanel} from "@/scenes.ui/update-force-panel";
import {ForceSpeedupPanel} from "@/scenes.ui/force-speedup-panel";
import {ForceMaxLevelPanel} from "@/scenes.ui/force-max-level-panel";
import ForceStore from "@/store/force";
import {ForceUpdateInfo, ForceUpgrade, ForceWithUpgrade} from "@/store/force/types";
import {ForceSlotInfoPanel} from "@/scenes.ui/force-slot-info-panel";
import {BottomHammersPanel} from "@/scenes.ui/bottom-hammers-panel";
import {CardPanel} from "@/scenes.ui/card-panel";
import {MainForcePanel} from "@/scenes.ui/main-force-panel";
import {ForceInfoPanel} from "@/scenes.ui/force-info-panel";
import {BuyHammerPanel} from "@/scenes.ui/BuyHammerPanel";
import {IntroImage, IntroPage, IntroText, MultipleIntroPanel} from "@/scenes.ui/multiple-intro-panel";
import CardStore from "@/store/card";
import {CardInfo, CardType} from "@/store/card/types";
import {TestLevelDiffsPanel} from "@/scenes.ui/test-level-diffs-panel";

@injectable()
export class PlayerCreatureService extends AbstractCreatureService implements Listener {

    private artifactLessonService!: ArtifactLessonService;
    private listService!: ListService;

    private slotTimerId: number | undefined;
    private forceSlotTimerId: number | undefined;
    private healthTimerId: number | undefined;

    private healthTimer: HealthTimerPanel | undefined = undefined;

    private shopCallback: (() => unknown) | undefined;

    private speedupPanel: ArtifactSpeedupPanel | undefined;
    private forceSpeedupPanel: ForceSpeedupPanel | undefined;

    private lessonInProgress: boolean = false;

    private static artifactIdToOpen: number | undefined;
    private static forceToOpen: WarriorClass | undefined;

    private openUnlockPanel: boolean = false;

    private addToSquadButton!: AddToSquadButton;

    private artifactInfos!: UpdateInfo[];
    private forceInfos!: ForceUpdateInfo[];
    private forceInfosCreatureId!: number;

    public initPlayerCreatureService(scene: Scene, canvas: HTMLCanvasElement, artifactLessonService: ArtifactLessonService, listService: ListService,
                                     creatureIndex: number, backCallback: () => unknown, shopCallback: () => unknown, openUnlockPanel: boolean): void {
        super.initPage(scene, canvas, creatureIndex, backCallback);

        this.shopCallback = shopCallback;
        this.openUnlockPanel = openUnlockPanel;

        this.artifactLessonService = artifactLessonService;
        this.listService = listService;
        this.lessonInProgress = artifactLessonService.isLessonInProgress();

        this.setupCreaturesPanel();

        this.refreshHealthTimer();

        let player = PlayerStore.getPlayer();
        if (player.stair >= 25) {
            let self = this;

            if(player.stair < 40) {
                let levelTitle = Tr('rang');
                this.addMenuButton(scene, 30, 30, Keys.ATLAS_FORCES_LARGE, 'ic_rang', levelTitle, () => {
                    self.initLevels(levelTitle);
                });
            }

            if(player.creatures.length >= 8) {
                this.addMenuButton(scene, canvas.width / CU.S - 35, 33, Keys.ICON_HEROES, undefined, Tr('geroi'), () => {
                    self.initCreatureListPanel(scene, canvas);
                });
            }

            if(PlayerStore.GET_CARDS_STATE()){
                this.addMenuButton(scene, canvas.width / CU.S - 100, 33, Keys.ICON_CARDS, undefined, Tr('cards'), () => {
                    CardStore.fetchCardsInfo().then((cards: CardInfo[]) => {
                        BasePanel.closeAll();
                        self.initCardsPanel(scene, canvas, cards);
                    });
                });
            }
        }

        if (player.stair >= 40) {
            this.tabButtons = [];
            this.tabs.push(this.addTabButton(scene, 27, 30, Tr('dari'), CreatureTabName.FORCES));
            this.tabs.push(this.addTabButton(scene, 125, 30, Tr('artifacts'), CreatureTabName.ARTIFACTS));
        }

        this.showNews(player, scene, canvas);
    }

    private showNews(player: Player, scene: Scene, canvas: HTMLCanvasElement) {
        if (player.creatureNews === CreatureNews.RANG) {
            let ip: SoloIntroPanel = new SoloIntroPanel(scene, canvas, Tr('news.rang.title'), Tr('news.rang.subtitle'),
                Keys.ATLAS_FORCES_LARGE, 'g_phoenix', Tr('news.rang.1'), Tr('news.rang.2'), () => {
                    ip.close();
                    PlayerStore.resetCreatureNews();
                });
            ip.openAnimated();
        } else if (player.creatureNews === CreatureNews.FORCES) {
            let ip: SoloIntroPanel = new SoloIntroPanel(scene, canvas, Tr('news.force.title'), Tr('news.force.subtitle'),
                Keys.ATLAS_FORCES_LARGE, 'g_dragon', Tr('news.force.1'), Tr('news.force.2'), () => {
                    ip.close();
                    PlayerStore.resetCreatureNews();
                });
            ip.openAnimated();
        } else if (player.creatureNews === CreatureNews.FORCES_FOR_40) {
            let ip: SoloIntroPanel = new SoloIntroPanel(scene, canvas, Tr('news.force.40.title').toUpperCase(), '',
                Keys.ATLAS_FORCES_LARGE_2, 'g_sorcerer', Tr('news.force.40.1').toUpperCase(), Tr('news.force.40.2'), () => {
                    ip.close();
                    PlayerStore.resetCreatureNews();
                });
            ip.openAnimated();
        } else if (player.creatureNews === CreatureNews.FORCES_FOR_500) {

            let ip: MultipleIntroPanel = new MultipleIntroPanel(scene, canvas, Tr('news.force.40.title').toUpperCase(),
                [

                    new IntroPage([
                        new IntroText(Tr('news.force.500.1').toUpperCase(), '#ffd465'),
                        new IntroText(Tr('news.force.500.2'), '#a2ff82'),
                        new IntroText(Tr('news.force.500.3'), '#ffd465'),
                        new IntroText(Tr('news.force.500.4'), '#a2ff82'),
                        ]
                    ),
                    new IntroPage([
                        new IntroText(Tr('news.force.500.5'), '#ffd465'),
                        new IntroImage(Keys.ATLAS_FORCES_LARGE_2, 'g_lord', 0, 45),
                        new IntroImage(Keys.ATLAS_FORCES_LARGE_3, 'g_druid', -120, -25),
                        new IntroImage(Keys.ATLAS_FORCES_LARGE_2, 'g_chaos', 120, -25),
                        ]
                    ),
                    new IntroPage([
                        new IntroText(Tr('news.force.500.6'), '#a2ff82'),
                        new IntroImage(Keys.ATLAS_FORCES_LARGE_2, 'g_lord', 0, 45),
                        new IntroImage(Keys.ATLAS_UI_FORCES, 'force-black-bg', 0, -25, 1, 0.2),
                        new IntroImage(Keys.ATLAS_UI_FORCES, 'force-glass-bg', 0, -25, 1.2),
                        new IntroImage(Keys.ATLAS_UI_FORCES, 'indicator-available', 40, 10, 1.2),
                        ], Tr('poniatno')
                    ),
                    new IntroPage([
                            new IntroText(Tr('news.force.500.7'), '#ffd465'),
                            new IntroImage(Keys.ATLAS_FORCES_LARGE_3, 'g_druid', 0, 45),
                            new IntroImage(Keys.ATLAS_UI_FORCES, 'indicator-can-upgrade', 0, 25, 1.2),
                        ],
                    ),
                    new IntroPage([
                            new IntroText(Tr('news.force.500.8'), '#a2ff82'),
                            new IntroText(Tr('news.force.500.9'), '#ffd465'),
                            new IntroText(Tr('news.force.500.10'), '#a2ff82'),
                        ]
                    ),
                    new IntroPage([
                            new IntroText(Tr('news.force.500.11'), '#a2ff82'),
                            new IntroText(Tr('news.force.500.12'), '#ffd465'),
                        ]
                    ),
                    new IntroPage([
                            new IntroText(Tr('news.force.500.13'), '#a2ff82'),
                        ]
                    ),
                    new IntroPage([
                            new IntroText(Tr('news.force.500.14'), '#ffd465'),
                        ], Tr('otlichno')
                    ),

                ],
                () => {
                    PlayerStore.resetCreatureNews();
                }, this.clickSound!);
            ip.openAnimated();

        } else if (player.creatureNews === CreatureNews.RANG_AND_FORCES) {
            let ip: SoloIntroPanel = new SoloIntroPanel(scene, canvas, Tr('news.rf.title'), Tr('news.rf.subtitle'),
                Keys.ATLAS_FORCES_LARGE, 'g_dragon', Tr('news.rf.1'), Tr('news.rf.2'), () => {
                    ip.close();
                });
            PlayerStore.resetCreatureNews();
            ip.openAnimated();
        }
    }

    // private initForces() {
    //     let self = this;
    //     const creatures = CreatureUtils.sortCreaturesDescending(CreatureUtils.findNonTemporalCreatures(PlayerStore.getPlayer().creatures)); //PlayerStore.getPlayer().creatures; sfd
    //     const creature: Creature = creatures[this.creatureIndex!];
    //
    //     if ((creature.warriorClass === WarriorClass.TITAN && creature.forces.length >= 7) || creature.warriorClass === WarriorClass.ANGEL || creature.warriorClass === WarriorClass.ASG_PHOENIX
    //                 || creature.warriorClass === WarriorClass.YOTUN || creature.warriorClass === WarriorClass.ARCHANGEL || creature.warriorClass === WarriorClass.ARCHITITAN
    //                 || creature.imageId === 'top-dragon-1' || creature.imageId === 'top-dragon-2' || creature.imageId === 'top-phoenix'
    //                 || creature.imageId === 'sur_rabbit' || creature.imageId === 'sur_sigi' || creature.imageId === 'sur_rokkara' || creature.imageId === 'sur_ns') {
    //         let ip: InfoPanel = new InfoPanel(self.scene!, self.canvas!, Tr('ups'),  Tr('no.forces.to.learn'),
    //             Tr('no.forces.to.learn2'), () => {});
    //         ip.openAnimated();
    //         return;
    //     }
    //
    //     let forcesTitle: string = TrSpace('forces') + creature.name.toUpperCase();
    //
    //     let blackouter: ListBlackouter = self.listService.showSmart(self.scene!, self.canvas!, () => {
    //         self.bottomPanel!.setDefaultDepth();
    //     });
    //
    //     ShopStore.fetchShopForces(creature.id).then((sfs: ShopForce[]) => {
    //         blackouter.notifyLoaded(() => {
    //             let members: Container[] = [];
    //             sfs.forEach(sf => {
    //                 let shopPanel = new ShopForcePanel(self.scene!, sf, () => {
    //                     if (PlayerStore.getPlayer().mithril < sf.price) {
    //                         SoundUtils.play(self.levelUpSound);
    //                         self.listService.close(forcesTitle);
    //                         self.shopCallback!();
    //                     } else {
    //                         SoundUtils.play(self.selectSound);
    //                         let confirmPanel: BuyConfirmationPanel = new BuyConfirmationPanel(self.scene!, self.canvas!, sf.name, sf.content, Tr('izuchaem.silu') + sf.name.substr(4) + '?', () => {
    //                             SoundUtils.play(self.awardSound);
    //                             ShopStore.buyForce(sf.warriorClass, sf.creatureId).then(() => {
    //                                 shopPanel.initActions(self.scene!, 0, true, Tr('imeetsja'), () => {});
    //                                 new FeedbackPanel(self.scene!, Tr('sila.izuchena'), undefined, true).show();
    //                             });
    //                         });
    //                         confirmPanel.open();
    //                     }
    //                 })
    //                 members.push(shopPanel);
    //             });
    //             members.push(new ListTextPanel(self.scene!, Tr('forces.are.cool'), 150));
    //             blackouter.notifyInitialized(members, forcesTitle);
    //             if (self.bottomPanel) {
    //                 self.bottomPanel.setDepth(ListService.DEPTH + 1);
    //             }
    //         });
    //     });
    // }

    private initCreatureListPanel(scene: Scene, canvas: HTMLCanvasElement) {
        let self = this;
        let creaturesGrid = new CreaturesGridView(scene, canvas, (creatureIndex:number) => {
            if(creatureIndex != -1) {
                self.select(creatureIndex);
                creaturesGrid.destroy();
            }
        });
        creaturesGrid.open();
        this.scene!.add.existing(creaturesGrid);
    }

    private initCardsPanel(scene: Scene, canvas: HTMLCanvasElement, cards:CardInfo[]) {
        let useCardCallback = (cardType:CardType) => {
            CardStore.useCard(cardType).then(() => {
                // new FeedbackPanel(scene, Tr('card.used'), undefined, true).show();
            });
        };
        let cardPanel = new CardPanel(scene, canvas, cards, useCardCallback, this.clickSound, this.completedSound);
        cardPanel.openAnimated();
        this.scene!.add.existing(cardPanel);
    }

    private initLevels(levelsTitle: string) {
        let self = this;

        const creatures = PlayerStore.getPlayer().creatures;
        const creature: Creature = creatures[this.creatureIndex!];

        if (creature.warriorClass === WarriorClass.WARLOCK) {
            let ip: InfoPanel = new InfoPanel(self.scene!, self.canvas!, Tr('ups'), Tr('ups.warlock.short'),
                Tr('ups.warlock'), () => {});
            ip.openAnimated();
            return;
        } else if (creature.warriorClass === WarriorClass.TITAN) {
            let ip: InfoPanel = new InfoPanel(self.scene!, self.canvas!, Tr('ups'),  Tr('ups.titan.short'),
                Tr('ups.titan'), () => {});
            ip.openAnimated();
            return;
        } else if (creature.warriorClass === WarriorClass.ARCHITITAN) {
            let ip: InfoPanel = new InfoPanel(self.scene!, self.canvas!, Tr('ups'),  Tr('ups.archititan.short'),
                Tr('ups.archititan'), () => {});
            ip.openAnimated();
            return;
        } else if (creature.warriorClass === WarriorClass.ANGEL) {
            let ip: InfoPanel = new InfoPanel(self.scene!, self.canvas!, Tr('ups'), Tr('ups.angel.short'),
                Tr('ups.angel'), () => {});
            ip.openAnimated();
            return;
        } else if (creature.warriorClass === WarriorClass.ARCHANGEL) {
            let ip: InfoPanel = new InfoPanel(self.scene!, self.canvas!, Tr('ups'), Tr('ups.archangel.short'),
                Tr('ups.archangel'), () => {});
            ip.openAnimated();
            return;
        } else if (creature.warriorClass === WarriorClass.YOTUN) {
            let ip: InfoPanel = new InfoPanel(self.scene!, self.canvas!, Tr('ups'), Tr('ups.yotun.short'),
                Tr('ups.yotun'), () => {});
            ip.openAnimated();
            return;
        } else if (creature.warriorClass === WarriorClass.ASG_PHOENIX) {
            let ip: InfoPanel = new InfoPanel(self.scene!, self.canvas!, Tr('ups'), Tr('ups.asg.phoenix.short'),
                Tr('ups.asg.phoenix'), () => {});
            ip.openAnimated();
            return;
        }

        let blackouter: ListBlackouter = self.listService.showSmart(self.scene!, self.canvas!, () => {
            self.bottomPanel!.setDefaultDepth();
        });

        CreatureStore.fetchLevelsInfo(creature.id).then((li: LevelsInfo) => {

            blackouter.notifyLoaded(() => {
                let members: Container[] = [];
                let levPanel: ClassLevelPanel = new ClassLevelPanel(self.scene!, creature, li, () => {
                    this.buyWithConfirmation(li.completePrice, levelsTitle, Tr('uroven') + ' ' + String(li.level + 1), Tr('otlichno.geroy.poluchit') + String(li.level + 1) + Tr('y.uroven.prjamo.seychas'), Tr('confirm'), () => {
                        CreatureStore.completeCreatureLevel(creature.id).then(() => {
                            CreatureStore.fetchLevelsInfo(creature.id).then((newLi: LevelsInfo) => {
                                levPanel.init(self.scene!, creature, newLi);
                            });
                            new FeedbackPanel(self.scene!, Tr('noviy.uroven.poluchen'), undefined, true).show();
                        });
                    });
                });
                members.push(levPanel);

                blackouter.notifyInitialized(members, levelsTitle);

                if (self.bottomPanel) {
                    self.bottomPanel.setDepth(ListService.DEPTH + 1);
                }
            });
        });
    }

    private buyWithConfirmation(price: number, title: string, confirmName: string, confirmContent: string, confirmAgitation: string, callback: () => unknown) {
        let self = this;
        if (PlayerStore.getPlayer().mithril < price) {
            SoundUtils.play(self.levelUpSound);
            self.listService.close(title);
            self.shopCallback!();
        } else {
            SoundUtils.play(self.selectSound);
            let confirmPanel: BuyConfirmationPanel = new BuyConfirmationPanel(self.scene!, self.canvas!, confirmName, confirmContent, confirmAgitation, () => {
                SoundUtils.play(self.awardSound);
                callback();
            });
            confirmPanel.open();
        }
    }

    public select(creatureIndex: number) {
        this.creatureIndex = creatureIndex;
        this.navPanel!.select(this.creatureIndex);
    }

    protected setupCreaturesPanel() {
        if (this.lessonInProgress) {
            let player = PlayerStore.getPlayer();
            let creatureWithArtifact: Creature = CreatureUtils.findFirstHavingArtifact(player)!;
            if (creatureWithArtifact) {
                this.creatureIndex = player.creatures.indexOf(creatureWithArtifact);
            } else {
                this.lessonInProgress = false;
            }
        }

        let reversedNonTemporalCreaturesList = CreatureUtils.findNonTemporalCreatures(CreatureUtils.sortCreaturesAscending(PlayerStore.getPlayer().creatures));
        let creaturesSize: number = reversedNonTemporalCreaturesList.length;

        ArtifactStore.actualizeArtifacts().then(() => {
            this.destroyNavPanel();

            let nonTemporalCreaturesList = CreatureUtils.findNonTemporalCreatures(CreatureUtils.sortCreaturesDescending(PlayerStore.getPlayer().creatures));
            const creature: Creature = nonTemporalCreaturesList[this.creatureIndex!];

            if (!this.lessonInProgress) {

                let selectCallback = (cr: Creature) => {
                    cr = PlayerStore.findCreatureById(cr.id)!;
                    this.creatureIndex = PlayerStore.getPlayer().creatures.indexOf(cr);
                    this.makeCharacterImage(cr);
                    this.setupSlots(cr);
                    // this.setupBottomForcesPanel(cr);
                    this.setupSquadAddButton(cr);
                    BasePanel.closeAll();
                    this.prepareCreatureStats(cr);
                };

                const orderedCreaturesList = CreatureUtils.getOrderedCreaturesList(reversedNonTemporalCreaturesList);

                if (creaturesSize <= 8) {
                    this.navPanel = new MiniCreaturesPanel(this.scene!, this.canvas!, 300, this.creatureNavPanelY,
                        selectCallback, reversedNonTemporalCreaturesList, this.creatureIndex!);
                } else {
                    this.navPanel = new UnlimitedCreaturesPanel(this.scene!, this.canvas!, 300, this.creatureNavPanelY,
                        selectCallback, orderedCreaturesList, creature);
                }
                this.scene!.add.existing(this.navPanel.getGameObject());

            }
            this.backButton.setVisible(!this.lessonInProgress)
            this.makeCharacterImage(creature);
            this.setupSlots(creature);
            this.setupSquadAddButton(creature);

            ListenerManager.addPlayerListener(this);

            this.prepareCreatureStats(nonTemporalCreaturesList[this.creatureIndex!]);
        });
    }

    private setupSquadAddButton(creature: Creature) {

        if (this.addToSquadButton) {
            this.addToSquadButton.destroy();
        }

        if (creature.combative) {
            return;
        }

        this.addToSquadButton = new AddToSquadButton(this.scene!, 0, this.getSlotPositionY(9) - 120, () => {

            let creaturePickPanel: CreaturePickPanel = new CreaturePickPanel(this.scene!, this.canvas!, PlayerStore.getCombativeCreatures(), creature,
                (desiredCreatureId: number, currentCreatureId: number) => {
                    CreatureStore.addCreatureToSquad(desiredCreatureId, currentCreatureId).then(() => {
                        new FeedbackPanel(this.scene!, creature.name + Tr('teper.v.otrjade'), undefined, true).show();
                    });
                    this.addToSquadButton.destroy();
                });
            creaturePickPanel.open();
        });

        this.centralContainer!.add(this.addToSquadButton);

    }

    private refreshHealthTimer() {
        let player = PlayerStore.getPlayer();
        if (this.healthTimerId) {
            clearInterval(this.healthTimerId);
            this.healthTimerId = undefined;
        }
        if (this.healthTimer) {
            this.healthTimer.setVisible(false);
            this.healthTimer.destroy();
            this.healthTimer = undefined;
        }
        if (player.healthRecoverAt && player.healthRecoverAt > 0) {
            this.healthTimer = new HealthTimerPanel(this.scene!, 0, this.canvas!.height - 55 * CU.S, player, () => {
                PlayerStore.fetchRestoreHealthOptions().then((options: RestoreOptions) => {
                    let func = () => {
                        BasePanel.closeAll();
                        if (this.healthTimerId) {
                            clearInterval(this.healthTimerId);
                        }
                        if (this.healthTimer) {
                            this.healthTimer.setVisible(false);
                            this.healthTimer.destroy();
                        }
                        new FeedbackPanel(self.scene!, Tr('zdorove.vosstanovleno'), undefined, true).show();
                    };
                    let restorePanel = new HealthRestorePanel(this.scene!, this.canvas!, options, () => {

                        if (options.price <= PlayerStore.getPlayer().mithril) {
                            SoundUtils.play(this.awardSound);
                            PlayerStore.restoreHealth(PayType.GEMS).then(() => {
                                func();
                            });
                        } else {
                            SoundUtils.play(this.levelUpSound);
                            this.shopCallback!();
                        }
                    }, () => {
                        if (options.rewardedWatchesToday < options.rewardedWatchesMax) {
                            RewardedUtils.watchRewarded(self.scene!, RewardedAdvType.HEALTH_RESTORE, () => {
                                PlayerStore.restoreHealth(PayType.ADV).then(() => {
                                    func();
                                });
                            });
                        } else {
                            new FeedbackPanel(this.scene!, TrSpace('2.mr') + String(options.rewardedWatchesMax) + Tr('t.p.d'), undefined, true).show();
                        }
                    });
                    SoundUtils.play(this.clickSound);
                    restorePanel.open();
                });
            });
            this.scene!.add.existing(this.healthTimer);

            let self = this;
            this.healthTimerId = setInterval(() => {
                if (this.healthTimer && this.healthTimer.active && this.scene!.scene.isActive()) {
                    let completed: boolean = this.healthTimer!.refreshTimer();
                    if (completed) {
                        clearInterval(self.healthTimerId);
                        PlayerStore.fetchPlayerSilent().then(() => {
                            if (self.navPanel) {
                                self.navPanel.refreshSkills();
                            }
                            self.refreshHealthTimer();
                        });
                    }
                }
            }, 1000);
        }
    }

    private setupSlots(creature: Creature, warriorClassToOpen?: WarriorClass) {

        //TEST FORCE DIFF TEXTS

        // ForceStore.getAllForcesDiffs().then((diffDescriptions: string[]) => {
        //     let testPanel = new TestLevelDiffsPanel(this.scene!, this.canvas!, diffDescriptions, 0, 0, this.centralContainer!);
        //     testPanel.open();
        // })

        switch(this.tabName){
            case CreatureTabName.ARTIFACTS:
                this.setupArtifactSlots(creature);
                this.setupBottomForcesPanel(creature)
                break;
            case CreatureTabName.FORCES:
                this.setupForceSlots(creature, warriorClassToOpen);
                this.setupBottomHammersPanel(creature, PlayerStore.getPlayer().creatures.flatMap(creature => creature.forceUpgrades));
                break;
        }
    }

    private async setupArtifactSlots(creature: Creature) {
        const self = this;
        // await ArtifactStore.artifactsReadyForUpdate().then((artifactIds: string[]) => {
        await ArtifactStore.artifactsInfoByCreature(creature.id).then((artifactInfos: UpdateInfo[]) => {
            this.artifactInfos = artifactInfos;
            this.destroySLots();
            if(BottomHammersPanel.instance){
                BottomHammersPanel.instance.destroy();
                BottomHammersPanel.instance = null;
            }
            let slotsCount = creature.slotsCount;
            let artifacts: Artifact[] = Object.assign([], creature.artifacts).sort((a: Artifact, b: Artifact) => {
                return a.id - b.id;
            });
            for (let num = 0; num < Sizes.MAX_ARTIFACT_SLOTS_COUNT; ++num) {
                let art = artifacts[num];
                let unlockState = PlayerCreatureService.getUnlockState(slotsCount, artifacts.length, num);
                // let mayUpdate = art && artifactIds.includes(art.artifactId) && !art.updatingTill;
                let mayUpdate = art && artifactInfos.filter(aa => aa.id == art.id && aa.canUpgrade).length > 0 && !art.updatingTill;
                let artifactSlot = new ArtifactSlot(this.scene!, this.getSlotPositionX(num),
                    this.getSlotPositionY(num), this.SLOT_SIZE_INNER, art, mayUpdate, unlockState, () => {
                        SoundUtils.play(this.selectSound);
                        if (art) {
                            if (art.updatingTill && art.updatingTill > TimeUtils.getServerTimeMs()) {
                                self.openSpeedupPanel(art, artifactSlot);
                            } else {
                                self.openUpdatePanel(art, artifactSlot);
                            }
                        } else {
                            switch (unlockState) {
                                case UnlockState.UNLOCKED:
                                    this.showSlotAlertWindow(artifactSlot, Tr('eto.svobodniy.slot.dlja.predmeta.'));
                                    break;
                                case UnlockState.LOCKED:
                                    this.showSlotAlertWindow(artifactSlot, Tr('etot.slot.poka.ne.dostupen'));
                                    break;
                                case UnlockState.CAN_UNLOCK:
                                    self.showSlotUnlockPanel(creature, artifactSlot);

                                    break;
                            }
                        }
                    });
                artifactSlot.refreshTimer();
                this.centralContainer!.add(artifactSlot);
                this.artifactSlots.push(artifactSlot);

                if (art && art.id === PlayerCreatureService.artifactIdToOpen && mayUpdate) {
                    this.openUpdatePanel(art, artifactSlot);
                    PlayerCreatureService.artifactIdToOpen = undefined;
                }
            }

            if (this.openUnlockPanel) {
                let slotToUnlock = this.artifactSlots.find((s: ArtifactSlot) => s.getUnlockState() == UnlockState.CAN_UNLOCK);
                if (slotToUnlock) {
                    this.showSlotUnlockPanel(creature, slotToUnlock!);
                }
                this.openUnlockPanel = false;
            }

            if (this.lessonInProgress) {
                this.artifactLessonService.onPageOpen(self.scene!, self.artifactSlots[0]);
            }
        });

        if (this.slotTimerId) {
            clearInterval(this.slotTimerId);
        }
        this.slotTimerId = setInterval(() => {
            let updated = false;
            for (let artSlot of this.artifactSlots) {
                if (artSlot.active) {
                    updated = true;
                    let timerFired: boolean = artSlot.refreshTimer();
                    if (timerFired) {
                        SoundUtils.play(this.completedSound);
                        ArtifactStore.actualizeArtifacts();
                        break;
                    }
                }
            }
            if (self.speedupPanel && self.speedupPanel.active) {
                updated = true;
                self.speedupPanel.refreshTimer();
            }
            if (!updated && this.slotTimerId) {
                clearInterval(this.slotTimerId);
                this.slotTimerId = undefined;
            }
        }, 1000);

    }

    private async setupForceSlots(creature: Creature, warriorClassToOpen?: WarriorClass) {
        const self = this;
        await ForceStore.forcesInfoByCreature(creature.id).then((forceInfos: ForceUpdateInfo[]) => {
            this.forceInfos = forceInfos;
            this.forceInfosCreatureId = creature.id;
            this.destroySLots();
            if(BottomForcesPanel.instance){
                BottomForcesPanel.instance.destroy();
                BottomForcesPanel.instance = null;
            }

            if(BottomHammersPanel.instance && forceInfos.length > 0){
                let maxRanks = forceInfos.map(f => f.maxLevel);
                let maxOfMax = maxRanks.reduce((max, current) => current > max ? current : max, maxRanks[0]);
                BottomHammersPanel.instance.setForcesMaxLevelLabel(maxOfMax);
            }

            let mainForce = new Force();
            mainForce.warriorClass = creature.warriorClass;
            mainForce.level = creature.level;

            let mainForceY = this.getMainForceSlotPositionY(5);
            const mainForceBg = SpriteUtils.addImage(this.scene!, 0, mainForceY,
                Keys.ATLAS_UI_FORCES,  Keys.UI_FORCES_MAIN_HIGHLIGHT, 1.2)
                .setOrigin(0.5, 0.5).setInteractive();
            SpriteUtils.setDispSize(mainForceBg, this.FORCE_SLOT_SIZE_INNER * 1.4 , this.FORCE_SLOT_SIZE_INNER * 1.4);
            mainForceBg.depth = 1100;

            const mainForceImage = SpriteUtils.addImage(this.scene!, 0, mainForceY,
                CreatureUtils.getForceAtlasLarge(mainForce.warriorClass), CreatureUtils.getLargeForceIcon(mainForce.warriorClass, true))
            .setOrigin(0.5, 0.5).setInteractive();

            let scale = 0.95;
            SpriteUtils.setDispSize(mainForceImage, this.FORCE_SLOT_SIZE_INNER * scale , this.FORCE_SLOT_SIZE_INNER * scale);
            mainForceImage.depth = 1100;

            this.centralContainer!.add(mainForceBg)
            this.centralContainer!.add(mainForceImage)

            this.mainForce = {sprite: mainForceImage, bg: mainForceBg};

            mainForceImage.on('pointerup', () => {
                CreatureStore.fetchShortLevelsInfo(creature.id).then((li: LevelsInfo) => {
                    BasePanel.closeAll();
                    let amlp = new MainForcePanel(this.scene!, this.canvas!, li, mainForce.warriorClass, creature.level, 0, mainForceY, this.centralContainer!, () => {
                        self.initLevels(Tr('hero.rang'));
                    });
                    amlp.open();
                });
            });

            // let forceUprgadabilityVisible = this.forceInfos.filter(f => f.canUpgrade && f.minMonsterLevel <= player.bestVictoryStair).length > 0;
            // let forceUpgradabilityInfoY = 130 + 25 + (90 - 25)/CU.S;
            // const forceUpgradabilityInfoBg = SpriteUtils.addImage(this.scene!, CU.CANVAS_WIDTH/2 /CU.S, forceUpgradabilityInfoY,
            //     Keys.ATLAS_UI_FORCES,  Keys.UI_FORCE_BLACK_BG, 1)
            //     .setOrigin(0.5, 0.5);
            // forceUpgradabilityInfoBg.depth = 1100;
            // forceUpgradabilityInfoBg.setVisible(forceUprgadabilityVisible);

            // const forceUpgradabilityInfoText = TextUtils.addTextAdvanced(this.scene!, Tr('can.improve.forces'), CU.CANVAS_WIDTH/2 /CU.S, forceUpgradabilityInfoY,
            //     '#e3ca8e',16,'normal', 400, 'center', 0, Fonts.BENGALY_NORMAL);
            // forceUpgradabilityInfoText.depth = 1100;
            // forceUpgradabilityInfoText.setStroke('#1d0709', 2)
            // forceUpgradabilityInfoText.setVisible(forceUprgadabilityVisible);
            //
            // this.forceUpgradabilityInfo = {bg: forceUpgradabilityInfoBg, text: forceUpgradabilityInfoText};


            let slotsCount = creature.slotsCount;
            let forces: Force[] = Object.assign([], creature.forcesV2).sort((a: Force, b: Force) => {
                return a.warriorClass.localeCompare(b.warriorClass);
            });
            for (let num = 0; num < Sizes.MAX_FORCES_SLOTS_COUNT; ++num) {
                let f:ForceUpdateInfo|undefined = forceInfos[num];

                let forceWithUpgrade = f? new ForceWithUpgrade() : undefined;
                if(f && forceWithUpgrade) {
                    let forceUpgrade = creature.forceUpgrades.filter(fu => fu.warriorClass == f!.warriorClass).shift();
                    forceWithUpgrade.warriorClass = f.warriorClass;
                    forceWithUpgrade.level = f.currentLevel;
                    forceWithUpgrade.creatureId = creature.id;
                    if (forceUpgrade) forceWithUpgrade.updatingTill = forceUpgrade.updatingTill;
                    forceWithUpgrade.canUpgrade = f.canUpgrade;
                    forceWithUpgrade.minLevelMonster = f.minMonsterLevel;
                }
                let mayUpdate = forceWithUpgrade && forceWithUpgrade.canUpgrade && !forceWithUpgrade.updatingTill;
                let unlockState = PlayerCreatureService.getUnlockState(slotsCount, forces.length, num);
                let forceSlot = new ForceSlot(this.scene!, this.getForcesSlotPositionX(num),
                    this.getForceSlotPositionY(num), this.FORCE_SLOT_SIZE_INNER, forceWithUpgrade!, () => {
                        SoundUtils.play(this.selectSound);
                        if (forceWithUpgrade) {
                            if (forceWithUpgrade.updatingTill > TimeUtils.getServerTimeMs()) {
                                self.openForceSpeedupPanel(forceWithUpgrade, forceSlot);
                            } else {
                                self.openForceUpdatePanel(forceWithUpgrade, forceSlot);
                            }
                        } else {
                            switch (unlockState) {
                                case UnlockState.UNLOCKED:
                                case UnlockState.LOCKED:
                                    this.showForceSlotAlertWindow(forceSlot, Tr('etot.slot.poka.ne.dostupen'));
                                    break;
                                case UnlockState.CAN_UNLOCK:
                                    self.showForceSlotUnlockPanel(creature, forceSlot);

                                    break;
                            }
                        }
                    });
                forceSlot.refreshTimer();
                if(warriorClassToOpen && warriorClassToOpen == forceWithUpgrade?.warriorClass){
                    forceSlot.emulateClick();
                }
                if(BottomHammersPanel.instance) BottomHammersPanel.instance.refreshTimer();
                this.centralContainer!.add(forceSlot);
                this.forceSlots.push(forceSlot);

                if (forceWithUpgrade && forceWithUpgrade.warriorClass === PlayerCreatureService.forceToOpen && mayUpdate) {
                    this.openForceUpdatePanel(forceWithUpgrade, forceSlot);
                    PlayerCreatureService.forceToOpen = undefined;
                }
            }

            if (this.openUnlockPanel) {
                let slotToUnlock = this.artifactSlots.find((s: ArtifactSlot) => s.getUnlockState() == UnlockState.CAN_UNLOCK);
                if (slotToUnlock) {
                    this.showSlotUnlockPanel(creature, slotToUnlock!);
                }
                this.openUnlockPanel = false;
            }
        });

        if (this.forceSlotTimerId) {
            clearInterval(this.forceSlotTimerId);
        }

        this.forceSlotTimerId = setInterval(() => {
            let updated = false;
            for (let forceSlot of this.forceSlots) {
                if (forceSlot.active) {
                    updated = true;
                    let timerFired: boolean = forceSlot.refreshTimer();
                    if (timerFired) {
                        SoundUtils.play(this.completedSound);
                        ForceStore.actualizeForces();
                        break;
                    }
                }
            }
            if (self.forceSpeedupPanel && self.forceSpeedupPanel.active) {
                updated = true;
                self.forceSpeedupPanel.refreshTimer();
            }
            if(BottomHammersPanel.instance && BottomHammersPanel.instance.active) BottomHammersPanel.instance.refreshTimer();
            if (!updated && this.forceSlotTimerId) {
                clearInterval(this.forceSlotTimerId);
                this.forceSlotTimerId = undefined;
            }
        }, 1000);
    }

    private setupBottomForcesPanel(creature: Creature) {
        if(BottomForcesPanel.instance){
            BottomForcesPanel.instance.destroy();
            BottomForcesPanel.instance = null;
        }

        let forces:Force[] = [];
        if(creature.forcesV2){
            forces = [...creature.forcesV2];
        }

        let rewriteForceLevel:number | null = null;
        if (creature.warriorClass == WarriorClass.WARLOCK) { //TODO дублирование логики с сервера
            rewriteForceLevel = 2;
        } else if (creature.warriorClass == WarriorClass.TITAN) {
            rewriteForceLevel =  3;
        } else if (creature.warriorClass == WarriorClass.ANGEL || creature.warriorClass == WarriorClass.ARCHITITAN) {
            rewriteForceLevel =  4;
        } else if (creature.warriorClass == WarriorClass.ASG_PHOENIX
            || creature.warriorClass == WarriorClass.YOTUN || creature.warriorClass == WarriorClass.ARCHANGEL) {
            rewriteForceLevel =  5;
        }

        forces = forces.sort((a, b) => -Object.values(WarriorClass).indexOf(b.warriorClass) + Object.values(WarriorClass).indexOf(a.warriorClass));
        forces = forces.filter(f => f.warriorClass != creature.warriorClass);

        if(forces.filter(f => f.warriorClass == creature.warriorClass).length == 0){
            if(rewriteForceLevel){
                forces.push({warriorClass: creature.warriorClass, level: 1});
            } else {
                forces.push({warriorClass: creature.warriorClass, level: creature.level});
            }
        }
        let bottomPanel = new BottomForcesPanel(this.scene!, this.canvas!, creature, forces.reverse(), this.canvas!.width / 2, this.canvas!.height, (wc:WarriorClass, fd:ForceDescription, maxRank:number)=>{
            this.listService.showForceRanksManual(this.scene!, this.canvas!, wc, fd, () => { }, maxRank);
        }, () => {
            if(this.forceInfosCreatureId && this.forceInfosCreatureId == creature.id){
                return Promise.resolve(this.forceInfos);
            }
            return ForceStore.forcesInfoByCreature(creature.id).then((data) => {
                this.forceInfos = data;
                this.forceInfosCreatureId = creature.id;
                return data;
            });
        });

        bottomPanel.setDepth(1100);
        this.scene!.add.existing(bottomPanel);
    }

    private setupBottomHammersPanel(creature: Creature, upgrades:ForceUpgrade[]) {
        let self = this;

        if(BottomHammersPanel.instance){
            BottomHammersPanel.instance.destroy();
            BottomHammersPanel.instance = null;
        }

        let bottomPanel = new BottomHammersPanel(this.scene!, this.canvas!, upgrades,
            this.canvas!.width / 2, this.canvas!.height, this.selectSound!, (cr:Creature, force:WarriorClass)=>{
                cr = PlayerStore.findCreatureById(cr.id)!;
                self.creatureIndex = PlayerStore.getPlayer().creatures.indexOf(cr);
                self.makeCharacterImage(cr);
                self.tabName = CreatureTabName.FORCES;
                self.setupSlots(cr, force);
                self.setupBottomForcesPanel(cr);
                self.setupSquadAddButton(cr);
                BasePanel.closeAll();
                self.prepareCreatureStats(cr);
                this.navPanel?.doSelect(cr);

            }, () => {
                let hammersCount = PlayerStore.GET_HAMMERS_COUNT();
                ForceStore.nextHammerPrice(hammersCount).then((price: number) => {
                    SoundUtils.play(this.selectSound);

                    let cp = new BuyHammerPanel(self.scene!, self.canvas!, hammersCount, price,() => {
                        let mithril = PlayerStore.getPlayer().mithril;
                        if(mithril < price){
                            new AlertDialog(this.scene!, this.canvas!, 300 * CU.S, this.canvas!.height / 2,
                                Tr('ups'), Tr('insufficient.emeralds') + ' (' + Tr('you.have') + mithril + ')', undefined, Tr('ok') ).open();
                        } else {
                            ForceStore.buyHammer(PlayerStore.GET_HAMMERS_COUNT()).then(() => {
                                SoundUtils.play(this.awardSound);
                                self.setupSlots(creature);
                            })
                        }
                    });
                    cp.openAnimated();
                });
        });
        bottomPanel.setDepth(1100);
        this.scene!.add.existing(bottomPanel);
    }

    private showSlotUnlockPanel(creature: Creature, artifactSlot: ArtifactSlot) {
        ArtifactStore.unlockInfo(creature.id).then((ui: UnlockInfo | undefined) => {
            if (!ui) {
                console.log("updateInfo not found!")
                return;
            }
            let sup: SlotUnlockPanel = new SlotUnlockPanel(this.scene!, this.canvas!, ui, artifactSlot.x, artifactSlot.y, () => {
                if (ui.price <= PlayerStore.getPlayer().mithril) {
                    SoundUtils.play(this.awardSound);
                    ArtifactStore.unlockSlot(creature.id).then(() => {
                        BasePanel.closeAll();
                    });
                } else {
                    SoundUtils.play(this.levelUpSound);
                    this.shopCallback!();
                }
            }, this.centralContainer!);
            BasePanel.closeAll();
            sup.open();
            this.centralContainer!.remove(artifactSlot);
            this.centralContainer!.add(artifactSlot);
        });
    }

    private showForceSlotUnlockPanel(creature: Creature, forceSlot: ForceSlot) {
        ArtifactStore.unlockInfo(creature.id).then((ui: UnlockInfo | undefined) => {
            if (!ui) {
                console.log("updateInfo not found!")
                return;
            }
            let sup: SlotUnlockPanel = new SlotUnlockPanel(this.scene!, this.canvas!, ui, forceSlot.x, forceSlot.y, () => {
                if (ui.price <= PlayerStore.getPlayer().mithril) {
                    SoundUtils.play(this.awardSound);
                    ArtifactStore.unlockSlot(creature.id).then(() => {
                        BasePanel.closeAll();
                    });
                } else {
                    SoundUtils.play(this.levelUpSound);
                    this.shopCallback!();
                }
            }, this.centralContainer!);
            BasePanel.closeAll();
            sup.open();
            this.centralContainer!.remove(forceSlot);
            this.centralContainer!.add(forceSlot);
        });
    }

    private startUpdate(art: Artifact, artifactSlot: ArtifactSlot) {
        SoundUtils.play(this.buildingSound);
        if (this.lessonInProgress) {
            this.artifactLessonService.onImproveClicked();
        }
        BasePanel.closeAll();
        ArtifactStore.startUpdate(art.id).then(() => {
            //do nothing
        });

        let infos = this.artifactInfos.filter(aa => aa.id == art.id);
        let si = infos.length > 0 ? infos[0] : undefined;
        if(si){
            si.updatingTill = TimeUtils.getServerTimeMs() + si.updateTimeSec * 1000;
        }
        this.openSpeedupPanel(art, artifactSlot);
    }

    private startUpdateForce(force: ForceWithUpgrade, forceSlot: ForceSlot, targetRank:number) {
        SoundUtils.play(this.buildingSound);
        if (this.lessonInProgress) {
            this.artifactLessonService.onImproveClicked();
        }
        BasePanel.closeAll();

        let instantly = PlayerStore.GET_CARDS_STATE() && PlayerStore.GET_CARDS_STATE()!.elephantActive;

        ForceStore.startForceUpdate(force.creatureId, force.warriorClass, targetRank).then(() => {
            if(instantly){
                PlayerStore.GET_CARDS_STATE()!.elephantActive = false;
            }
        });

        let infos = this.forceInfos.filter(aa => aa.warriorClass == force.warriorClass);
        let si = infos.length > 0 ? infos[0] : undefined;
        if(si){
            si.updatingTill = TimeUtils.getServerTimeMs() + si.updateTimeSec * 1000;
        }
        if(!instantly){
            this.openForceSpeedupPanel(force, forceSlot);
        }
    }

    private openReplaceMenu(art: Artifact) {
        SoundUtils.play(this.selectSound);

        if (this.lessonInProgress) {
            new FeedbackPanel(this.scene!, Tr('snachala.zavershim.obuchenie'), undefined, true).show();
        } else {
            ArtifactStore.replaceInfo(art.id).then((ai: ArtifactInfo) => {
                let pickArtPanel = new ArtifactPickPanel(this.scene!, this.canvas!, Mode.REPLACING, ai,
                    () => {
                    }, (creatureId: number) => {
                        ArtifactStore.replaceArtifact(art.id, creatureId);
                    }, undefined, () => this.replacingWithNoSlots());
                pickArtPanel.openAnimated();
            });
        }
    }

    private replacingWithNoSlots() {
        const player: Player = PlayerStore.getPlayer();
        const nonTemporalCreatures = CreatureUtils.findNonTemporalCreatures(CreatureUtils.sortCreaturesDescending(player.creatures));
        const currentCreature: Creature = nonTemporalCreatures[this.creatureIndex!];
        let creature = CreatureUtils.findFirstCreatureAbleToGetArtifact(player, Sizes.MAX_ARTIFACT_SLOTS_COUNT, currentCreature);
        this.creatureIndex = nonTemporalCreatures.indexOf(creature!);
        this.select(this.creatureIndex!)
        this.makeCharacterImage(creature!);
        this.setupArtifactSlots(creature!).then(() => {
            let slotToUnlock = this.artifactSlots.find((s: ArtifactSlot) => s.getUnlockState() == UnlockState.CAN_UNLOCK);
            this.showSlotUnlockPanel(creature!, slotToUnlock!);
        });
        this.setupBottomForcesPanel(creature!);

    }

    private showSlotAlertWindow(slot: ArtifactSlot, text: string) {
        BasePanel.closeAll();
        new SlotInfoPanel(this.scene!, this.canvas!, Tr('slot'), text, slot.x, slot.y, this.centralContainer!).open();
        this.centralContainer!.remove(slot);
        this.centralContainer!.add(slot);
    }

    private showForceSlotAlertWindow(slot: ForceSlot, text: string) {
        BasePanel.closeAll();
        new ForceSlotInfoPanel(this.scene!, this.canvas!, Tr('slot'), text, slot.x, slot.y, this.centralContainer!).open();
        this.centralContainer!.remove(slot);
        this.centralContainer!.add(slot);
    }

    private openUpdatePanel(art: Artifact, artifactSlot: ArtifactSlot) {
        let infos = this.artifactInfos.filter(aa => aa.id == art.id);
        let ui = infos.length > 0 ? infos[0] : undefined;
        let self = this;
        // ArtifactStore.updateInfo(art.id).then((ui: UpdateInfo| undefined) => {
            if (!ui) {
                console.log("updateInfo not found! " + art.id)
                return;
            }
            if (ui.nextLevelValue && ui.nextLevelValue > 0) {
                BasePanel.closeAll();
                const uap = new UpdateArtifactPanel(self.scene!, self.canvas!, ui,
                    () => {
                        self.openReplaceMenu(art);
                    },
                    () => {
                        if (ui && ui.updateOverCharge > 0) {
                            SoundUtils.play(this.selectSound);
                            let aop: OverchargePanel = new OverchargePanel(self.scene!, self.canvas!,
                                0, 327, ui.amount, ui.updateOverCharge, () => {
                                    if (ui && ui.updateOverCharge <= PlayerStore.getPlayer().mithril) {
                                        this.startUpdate(art, artifactSlot);
                                    } else {
                                        SoundUtils.play(this.levelUpSound);
                                        this.shopCallback!();
                                    }
                                }, self.centralContainer!);
                            SpriteUtils.scale(aop, 1 / CU.S / CU.S, 1 / CU.S / CU.S);
                            aop.open();
                        } else {
                            this.startUpdate(art, artifactSlot);
                        }
                    }, (alertText) => {
                        SpriteUtils.scale(new AlertDialog(this.scene!, this.canvas!, CU.CANVAS_WIDTH / 2, this.canvas!.height / 2, Tr('ups'), alertText), 0.9).open();
                    }, artifactSlot.x, artifactSlot.y, self.centralContainer!);
                if (self.lessonInProgress) {
                    self.artifactLessonService.onUpdatePanelOpened(uap);
                }
                if (self.lessonInProgress) {
                    uap.setCloseListener(() => {
                        self.artifactLessonService.onPageOpen(self.scene!, self.artifactSlots[0]);
                    });
                }
                uap.open();
                self.centralContainer!.remove(artifactSlot);
                self.centralContainer!.add(artifactSlot);
            } else {
                BasePanel.closeAll();
                let amlp = new ArtifactMaxLevelPanel(this.scene!, this.canvas!, ui, () => {
                    self.openReplaceMenu(art);
                    amlp.close();
                }, artifactSlot.x, artifactSlot.y, this.centralContainer!);
                amlp.open();
                self.centralContainer!.remove(artifactSlot);
                self.centralContainer!.add(artifactSlot);
            }
        // });
    }

    private openForceUpdatePanel(force: ForceWithUpgrade, forceSlot: ForceSlot) {
        let infos = this.forceInfos.filter(aa => aa.warriorClass == force.warriorClass);
        let ui = infos.length > 0 ? infos[0] : undefined;
        let self = this;
        if (!ui) {
            console.log("updateInfo not found! " + force.warriorClass)
            return;
        }
        if (ui.canUpgrade) {
            BasePanel.closeAll();
            const uap = new UpdateForcePanel(self.scene!, self.canvas!, ui,
                () => {
                    if (ui && ui.updateOverCharge > 0) {
                        SoundUtils.play(this.selectSound);
                        let aop: OverchargePanel = new OverchargePanel(self.scene!, self.canvas!,
                            0, 327, ui.amount, ui.updateOverCharge, () => {
                                if (ui && ui.updateOverCharge <= PlayerStore.getPlayer().mithril) {
                                    this.startUpdateForce(force, forceSlot, ui.nextLevel);
                                } else {
                                    SoundUtils.play(this.levelUpSound);
                                    this.shopCallback!();
                                }
                            }, self.centralContainer!);
                        SpriteUtils.scale(aop, 1 / CU.S / CU.S, 1 / CU.S / CU.S);
                        aop.open();
                    } else {
                        this.startUpdateForce(force, forceSlot, ui!.nextLevel);
                    }
                }, (alertText:string) => {
                    SpriteUtils.scale(new AlertDialog(this.scene!, this.canvas!, CU.CANVAS_WIDTH / 2, this.canvas!.height / 2, Tr('ups'), alertText), 0.9).open();
                }, forceSlot.x, forceSlot.y, self.centralContainer!, (wc, rankLimit) => {
                    let desc = PlayerStore.GET_FORCE_DESCRIPTION(wc);
                    if(desc == undefined){
                        PlayerStore.getForceDescription(wc).then((forceDescription) => {
                            SoundUtils.play(this.selectSound);
                            this.listService.showForceRanksManual(this.scene!, this.canvas!, wc, forceDescription, () => { }, rankLimit);
                        });
                    } else {
                        SoundUtils.play(this.selectSound);
                        this.listService.showForceRanksManual(this.scene!, this.canvas!, wc, desc, () => { }, rankLimit);
                    }
                });
            uap.open();
            self.centralContainer!.remove(forceSlot);
            self.centralContainer!.add(forceSlot);
        } else {
            BasePanel.closeAll();
            let amlp = new ForceMaxLevelPanel(this.scene!, this.canvas!, ui, forceSlot.x, forceSlot.y, this.centralContainer!);
            amlp.open();
            self.centralContainer!.remove(forceSlot);
            self.centralContainer!.add(forceSlot);
        }
    }

    private openSpeedupPanel(art: Artifact, slot: ArtifactSlot) {
        let infos = this.artifactInfos.filter(aa => aa.id == art.id);
        let si = infos.length > 0 ? infos[0] : undefined;
        if (si) {
            BasePanel.closeAll();
            this.speedupPanel = new ArtifactSpeedupPanel(this.scene!, this.canvas!, slot.x, slot.y, si, () => {
                if (si && si.speedupPrice <= PlayerStore.getPlayer().mithril) {
                    SoundUtils.play(this.clickSound);
                    SoundUtils.play(this.completedSound);
                    BasePanel.closeAll();
                    ArtifactStore.speedupUpdate(art.id).then(() => {
                        PlayerCreatureService.artifactIdToOpen = art.id;
                    });
                } else {
                    SoundUtils.play(this.levelUpSound);
                    this.shopCallback!();
                }
            }, this.centralContainer!);
            this.speedupPanel.open();
            this.centralContainer!.remove(slot);
            this.centralContainer!.add(slot);
        }
    }

    private openForceSpeedupPanel(force: ForceWithUpgrade, slot: ForceSlot) {
        let infos = this.forceInfos.filter(aa => aa.warriorClass == force.warriorClass);
        let si = infos.length > 0 ? infos[0] : undefined;
        if (si) {
            BasePanel.closeAll();
            this.forceSpeedupPanel = new ForceSpeedupPanel(this.scene!, this.canvas!, slot.x, slot.y, si, () => {
                if (si && si.speedupPrice <= PlayerStore.getPlayer().mithril) {
                    SoundUtils.play(this.clickSound);
                    SoundUtils.play(this.completedSound);
                    BasePanel.closeAll();
                    ForceStore.speedupForceUpdate(force.creatureId, force.warriorClass).then(() => {
                        PlayerCreatureService.forceToOpen = force.warriorClass;
                    });
                } else {
                    SoundUtils.play(this.levelUpSound);
                    this.shopCallback!();
                }
            }, this.centralContainer!, (wc, rankLimit) => {
                let desc = PlayerStore.GET_FORCE_DESCRIPTION(wc);
                if(desc == undefined){
                    PlayerStore.getForceDescription(wc).then((forceDescription) => {
                        SoundUtils.play(this.selectSound);
                        this.listService.showForceRanksManual(this.scene!, this.canvas!, wc, forceDescription, () => { }, rankLimit);
                    });
                } else {
                    SoundUtils.play(this.selectSound);
                    this.listService.showForceRanksManual(this.scene!, this.canvas!, wc, desc, () => { }, rankLimit);
                }
            });
            this.forceSpeedupPanel.open();
            this.centralContainer!.remove(slot);
            this.centralContainer!.add(slot);
        }
    }

    public setArtifactIdToOpen(artifactIdToOpen: number) {
        PlayerCreatureService.artifactIdToOpen = artifactIdToOpen;
    }

    onChange(): void {
        if (this.scene!.scene.isActive()) {

            const creatures = PlayerStore.getPlayer().creatures;
            const creature: Creature = creatures[this.creatureIndex!];

            if (this.navPanel && this.navPanel.getActive()) {
                this.navPanel!.updateCreaturePanel(creatures);
                this.navPanel!.refreshSkills();
            }

            this.setupSlots(creature);
            // this.setupBottomForcesPanel(creature);
            this.bottomPanel!.setBalance(PlayerStore.getPlayer());
            if (this.lessonInProgress) {
                this.artifactLessonService.onChange(this.backButton);
            }
        }
    }
    private addMenuButton(scene: Scene, x: number, y: number, atlasKey: string, imageKey: string | undefined, name: string, callback: () => unknown): Sprite {
        let button = SpriteUtils.addSprite(scene, x, y, atlasKey, imageKey);
        if(name){
            TextUtils.addTextAdvanced(scene, name, button.x / CU.S, button.y / CU.S + 40,
                '#ffffff', 10, 'normal', 90, 'center', 1, Fonts.ARIAL_MT);
        }
        button.setInteractive().on('pointerup', () => {
            OnceCaller.doOnce('clan_' + imageKey, callback);
        }, button);
        return button;
    }

    private addTabButton(scene: Scene, x: number, y: number, name: string, tab:CreatureTabName): {tab: CreatureTabName, sprite: Sprite, label:Phaser.GameObjects.Text} {
        let isActive = this.tabName == tab;
        let atlasKey = Keys.ATLAS_UI_FORCES;
        let imageKey = isActive? Keys.UI_FORCES_TAB_ACTIVE : Keys.UI_FORCES_TAB_INACTIVE;
        let button = SpriteUtils.addSprite(scene, x, y, atlasKey, imageKey, 1.1);
        let text =  TextUtils.addTextAdvanced(scene, name, button.x / CU.S + 2, button.y / CU.S -10,
            isActive ? '#ffffff' : '#746061', name.length > 6? 10 : 11, 'normal', 90, 'center', 1, Fonts.ARIAL_MT);

        button.setInteractive().on('pointerup', () => {
            OnceCaller.doOnce('tab_' + imageKey, () => {
                if(this.tabName != tab) {
                    this.tabName = tab;
                    this.tabs.forEach(t => {
                        t.sprite.depth = t.tab == tab? 1 : 0;
                        t.label.depth = t.tab == tab? 1 : 0;
                        try {
                            t.label.setColor(t.tab == tab ? '#ffffff' : '#746061')
                        } catch (e) {
                            console.log('error setting color for label')
                        }
                        if(t.sprite.scene){
                            t.sprite.setTexture(Keys.ATLAS_UI_FORCES, t.tab == tab? Keys.UI_FORCES_TAB_ACTIVE : Keys.UI_FORCES_TAB_INACTIVE)
                        }
                        t.label.x = this.tabName == CreatureTabName.ARTIFACTS? t.label.x  - 10 * CU.S : t.label.x  + 10 * CU.S;
                    });
                    this.tabButtons.forEach(button => {button.setInteractive();});
                    button.disableInteractive();

                    BasePanel.closeAll();
                    this.setupSlots(PlayerStore.getPlayer().creatures[this.creatureIndex!]);
                }
            });
        }, button);

        if(isActive){
            button.disableInteractive();
        }

        this.tabButtons.push(button);
        return {tab: tab, sprite: button, label: text};

    }

}

export enum CreatureTabName {
    ARTIFACTS = "ARTIFACTS",
    FORCES = "FORCES",
}
